import { defineComponent as _defineComponent } from 'vue';
import { resolveDynamicComponent as _resolveDynamicComponent, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, mergeProps as _mergeProps, withCtx as _withCtx, toDisplayString as _toDisplayString, Fragment as _Fragment } from "vue";
const _hoisted_1 = {
    key: 4,
    class: "btn__loader"
};
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import debounce from 'lodash/debounce';
import { clearTimeout, setTimeout } from '@/utils/global';
export default /*@__PURE__*/ _defineComponent({
    __name: 'button-v2',
    props: {
        tag: { default: 'button' },
        type: { default: 'button' },
        size: { default: 'default-2' },
        theme: { default: 'primary' },
        disabled: { type: Boolean, default: false },
        active: { type: Boolean, default: false },
        activeTheme: { default: null },
        helperText: { default: null },
        extraClass: { default: null },
        extraClasses: { default: () => [] },
        icon: { default: null },
        leftIcon: { default: null },
        rightIcon: { default: null },
        leftIconClasses: { default: () => [] },
        rightIconClasses: { default: () => [] },
        href: { default: null },
        target: { default: '_self' },
        loading: { type: [Boolean, null], default: null },
        mobileLeftIconVisible: { type: Boolean, default: true },
        mobileRightIconVisible: { type: Boolean, default: true },
        mobileIconOnly: { type: Boolean, default: false }
    },
    emits: ['click'],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const windowWidth = ref(window.innerWidth);
        const isLoading = ref(false);
        const timeout = ref(null);
        const observeWindow = debounce(() => {
            windowWidth.value = window.innerWidth;
        }, 150);
        onMounted(() => {
            window.addEventListener('resize', observeWindow);
        });
        onUnmounted(() => {
            window.removeEventListener('resize', observeWindow);
        });
        watch(() => props.loading, (newValue) => {
            if (newValue) {
                timeout.value = setTimeout(() => {
                    isLoading.value = true;
                }, 200);
            }
            else {
                if (timeout.value) {
                    clearTimeout(timeout.value);
                    timeout.value = null;
                }
                isLoading.value = false;
            }
        }, { immediate: true });
        const isMobile = computed(() => windowWidth.value <= 768);
        const buttonResponsiveSize = computed(() => {
            if (!isMobile.value) {
                return props.size;
            }
            switch (props.size) {
                case 'large':
                    return 'large';
                default:
                    return props.size;
            }
        });
        const iconSize = computed(() => {
            var _a;
            const sizeMap = {
                large: 'icon--medium',
                big: 'icon--medium',
                default: 'icon--small',
                'default-2': 'icon--small',
                small: 'icon--small',
                mini: 'icon--mini',
                tiny: 'icon--tiny',
            };
            return (_a = sizeMap[buttonResponsiveSize.value]) !== null && _a !== void 0 ? _a : 'icon--small';
        });
        const classList = computed(() => {
            const theme = props.active && props.activeTheme ? `btn-v2--${props.activeTheme}` : `btn-v2--${props.theme}`;
            return [
                'btn-v2',
                `btn-v2--${buttonResponsiveSize.value}`,
                theme,
                'ledger-font',
                props.active ? 'active' : '',
                props.extraClass || '',
                props.icon || !showText.value ? `btn-v2-icon-only--${props.size}` : '',
                ...(props.extraClasses || [])
            ];
        });
        const helperTextClass = computed(() => {
            const sizeMap = {
                large: 'v2-text--big',
                big: 'v2-text--big',
                default: 'v2-text--base',
                'default-2': 'v2-text--base',
                small: 'v2-text--mini',
                mini: 'v2-text--mini',
                tiny: 'v2-text--mini',
            };
            return sizeMap[buttonResponsiveSize.value] || 'v2-text--base';
        });
        const iconClassList = computed(() => {
            return [iconSize.value].join(' ');
        });
        const leftIconVisible = computed(() => {
            if (!props.mobileLeftIconVisible && isMobile.value) {
                return false;
            }
            return Boolean(props.leftIcon);
        });
        const rightIconVisible = computed(() => {
            if (!props.mobileRightIconVisible && isMobile.value) {
                return false;
            }
            return Boolean(props.rightIcon);
        });
        const leftIconClassList = computed(() => {
            const classes = [
                iconSize.value,
                ...props.leftIconClasses
            ];
            if (showText.value) {
                classes.push('mr-tiny');
            }
            return classes.join(' ');
        });
        const rightIconClassList = computed(() => {
            const classes = [
                iconSize.value,
                ...props.leftIconClasses
            ];
            if (showText.value) {
                classes.push('ml-tiny');
            }
            return classes.join(' ');
        });
        const showText = computed(() => {
            return !(isMobile.value && props.mobileIconOnly);
        });
        function handleClick(event) {
            if (props.disabled) {
                event.preventDefault();
                return;
            }
            emit('click', event);
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                (_openBlock(), _createBlock(_resolveDynamicComponent(props.tag), _mergeProps(_ctx.$attrs, {
                    class: classList.value,
                    onClick: handleClick,
                    type: __props.tag === 'button' ? props.type : undefined,
                    disabled: __props.tag === 'button' ? props.disabled : undefined,
                    href: __props.tag === 'a' ? props.href : undefined,
                    target: __props.tag === 'a' ? props.target : undefined
                }), {
                    default: _withCtx(() => [
                        (props.icon)
                            ? (_openBlock(), _createBlock(_resolveDynamicComponent(props.icon), {
                                key: 0,
                                class: _normalizeClass(iconClassList.value)
                            }, null, 8, ["class"]))
                            : _createCommentVNode("", true),
                        (leftIconVisible.value)
                            ? (_openBlock(), _createBlock(_resolveDynamicComponent(props.leftIcon), {
                                key: 1,
                                class: _normalizeClass(leftIconClassList.value)
                            }, null, 8, ["class"]))
                            : _createCommentVNode("", true),
                        (showText.value)
                            ? _renderSlot(_ctx.$slots, "default", { key: 2 })
                            : _createCommentVNode("", true),
                        (rightIconVisible.value)
                            ? (_openBlock(), _createBlock(_resolveDynamicComponent(props.rightIcon), {
                                key: 3,
                                class: _normalizeClass(rightIconClassList.value)
                            }, null, 8, ["class"]))
                            : _createCommentVNode("", true),
                        (isLoading.value === true)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [...(_cache[0] || (_cache[0] = [
                                    _createElementVNode("i", { class: "icon-spinner9 spinner" }, null, -1)
                                ]))]))
                            : _createCommentVNode("", true)
                    ]),
                    _: 3
                }, 16, ["class", "type", "disabled", "href", "target"])),
                (props.helperText)
                    ? (_openBlock(), _createElementBlock("span", {
                        key: 0,
                        class: _normalizeClass(["text-v2-secondary mt-2 d-block", helperTextClass.value])
                    }, _toDisplayString(props.helperText), 3))
                    : _createCommentVNode("", true)
            ], 64));
        };
    }
});
